import type { AppRole } from "@/hooks/useUserRoles";

/**
 * Fine-grained permission keys.
 * Screens and actions should check a permission — never a raw role — so that
 * role definitions can change in one place.
 */
export const PERMISSIONS = [
  // Patients
  "patients.view",
  "patients.create",
  "patients.edit",
  "patients.delete",
  // Appointments & queue
  "appointments.view",
  "appointments.create",
  "appointments.reschedule",
  "appointments.cancel",
  "queue.view",
  "queue.manage",
  "checkin.manage",
  "display.manage",
  // Clinical
  "clinical.view",
  "prescriptions.view",
  "prescriptions.create",
  "labs.manage",
  "consultation.conduct",
  // Finance
  "billing.view",
  "billing.collect",
  "billing.manage",
  "inventory.manage",
  // Practice administration
  "staff.manage",
  "chambers.manage",
  "settings.manage",
  "audit.view",
  "subscription.manage",
  // Growth / marketing / website
  "analytics.view",
  "website.manage",
  "marketing.manage",
  "ai.use",
  // Platform
  "platform.admin",
] as const;

export type Permission = (typeof PERMISSIONS)[number];

const RECEPTIONIST: Permission[] = [
  "patients.view",
  "patients.create",
  "patients.edit",
  "appointments.view",
  "appointments.create",
  "appointments.reschedule",
  "appointments.cancel",
  "queue.view",
  "queue.manage",
  "checkin.manage",
  "display.manage",
  "billing.view",
  "billing.collect",
];

const DOCTOR: Permission[] = [
  "patients.view",
  "patients.create",
  "patients.edit",
  "appointments.view",
  "appointments.create",
  "appointments.reschedule",
  "appointments.cancel",
  "queue.view",
  "queue.manage",
  "clinical.view",
  "prescriptions.view",
  "prescriptions.create",
  "labs.manage",
  "consultation.conduct",
  "billing.view",
  "analytics.view",
  "ai.use",
];

const ADMIN: Permission[] = [...PERMISSIONS].filter((p) => p !== "platform.admin");

/** Role → permission matrix. Users get the union of all their roles. */
export const ROLE_PERMISSIONS: Record<AppRole, Permission[]> = {
  admin: ADMIN,
  doctor: DOCTOR,
  receptionist: RECEPTIONIST,
  // Legacy "staff" = front desk without money handling.
  staff: RECEPTIONIST.filter((p) => p !== "billing.collect"),
  viewer: [
    "patients.view",
    "appointments.view",
    "queue.view",
    "prescriptions.view",
    "billing.view",
    "analytics.view",
  ],
  platform_admin: [...PERMISSIONS],
};

export const ROLE_LABELS: Record<AppRole, { label: string; bn: string }> = {
  admin: { label: "Practice owner", bn: "প্র্যাকটিস মালিক" },
  doctor: { label: "Doctor", bn: "ডাক্তার" },
  receptionist: { label: "Receptionist", bn: "রিসেপশনিস্ট" },
  staff: { label: "Support staff", bn: "সহকারী" },
  viewer: { label: "Viewer", bn: "পর্যবেক্ষক" },
  platform_admin: { label: "Platform admin", bn: "প্ল্যাটফর্ম অ্যাডমিন" },
};

export function permissionsForRoles(roles: AppRole[]): Set<Permission> {
  const set = new Set<Permission>();
  roles.forEach((r) => ROLE_PERMISSIONS[r]?.forEach((p) => set.add(p)));
  return set;
}

/**
 * Route → required permission. Routes not listed are visible to any signed-in
 * user (marketing/help pages, personal profile, etc.).
 */
export const ROUTE_PERMISSIONS: Record<string, Permission> = {
  "/patients": "patients.view",
  "/appointments": "appointments.view",
  "/appointments/calendar": "appointments.view",
  "/appointments/today": "appointments.view",
  "/queue": "queue.manage",
  "/doctor-queue": "consultation.conduct",
  "/consultation": "consultation.conduct",
  "/reception": "checkin.manage",
  "/reception-queue": "checkin.manage",
  "/patient-checkin": "checkin.manage",
  "/display-control": "display.manage",
  "/settings/display": "display.manage",
  "/prescriptions": "prescriptions.view",
  "/medicines": "clinical.view",
  "/my-library": "clinical.view",
  "/templates": "clinical.view",
  "/lab-orders": "labs.manage",
  "/vaccinations": "clinical.view",
  "/clinical-guidelines": "clinical.view",
  "/research": "clinical.view",
  "/teleconsultation": "consultation.conduct",
  "/recalls": "marketing.manage",
  "/referrals": "clinical.view",
  "/patient-feedback": "analytics.view",
  "/billing": "billing.view",
  "/daily-income": "billing.manage",
  "/inventory": "inventory.manage",
  "/chambers": "chambers.manage",
  "/staff": "staff.manage",
  "/schedule": "chambers.manage",
  "/communications": "marketing.manage",
  "/audit-log": "audit.view",
  "/access-report": "audit.view",
  "/settings/data-export": "settings.manage",
  "/subscription": "subscription.manage",
  "/analytics": "analytics.view",
  "/chamber-insights": "analytics.view",
  "/reports": "analytics.view",
};

export function routePermission(path: string): Permission | undefined {
  return ROUTE_PERMISSIONS[path];
}

/** Website / online presence / AI sections share a coarse permission. */
export function sectionPermission(title: string): Permission | undefined {
  if (title === "Website" || title === "Online presence") return "website.manage";
  if (title === "AI & Growth") return "ai.use";
  return undefined;
}
